import {
  AGG_DELIMITER,
  type ColumnConfig,
  type ColumnsConfig,
} from "../rift/types";
import {
  type ColumnFormat,
  coerceColumnFormat,
  defaultFormatForPrimitive,
} from "../types/column-format";
import type { PrimitiveDataType } from "../types/primitive-data-type";

/**
 * Normalizes a single column config entry, upconverting the legacy
 * `{ formatType, formatOptions }` shape into the `{ format }` discriminated
 * union and dropping the legacy keys.
 */
const normalizeColumnConfig = (
  entry: ColumnConfig | undefined,
): ColumnConfig => {
  if (!entry) return {};
  const { format, ...rest } = entry as ColumnConfig & {
    formatType?: string | null;
    formatOptions?: unknown;
  };
  const coerced = coerceColumnFormat(format ?? entry);
  // Strip any legacy fields that may still be present on persisted data.
  delete (rest as Record<string, unknown>).formatType;
  delete (rest as Record<string, unknown>).formatOptions;
  return coerced ? { ...rest, format: coerced } : { ...rest };
};

/** Normalizes every entry of a columns config (legacy upconvert). */
export const normalizeColumnsConfig = (
  config: ColumnsConfig | null | undefined,
): ColumnsConfig => {
  if (!config) return {};
  const out: ColumnsConfig = {};
  for (const [key, value] of Object.entries(config)) {
    out[key] = normalizeColumnConfig(value);
  }
  return out;
};

/**
 * Merges column configs in order. Later configs override earlier ones per column.
 * Used at render time for blocks (e.g. dataset config + block overrides).
 *
 * Each input is normalized first, so legacy `{ formatType, formatOptions }`
 * entries are upconverted to `{ format }` and later configs win cleanly.
 */
export function mergeColumnsConfigs(
  ...columnConfigs: (ColumnsConfig | null | undefined)[]
): ColumnsConfig {
  const defined = columnConfigs
    .filter(Boolean)
    .map((config) => normalizeColumnsConfig(config));
  if (defined.length === 0) return {};

  const allKeys = new Set(defined.flatMap((config) => Object.keys(config)));

  const merged: ColumnsConfig = {};
  for (const key of allKeys) {
    merged[key] = Object.assign(
      {},
      ...defined.map((config) => config[key] ?? {}),
    );
  }

  return merged;
}

/**
 * Strips the aggregate suffix from a rendered column accessor.
 *
 * Rift aliases aggregated columns as `column::OP` (e.g. `revenue::SUM`). The
 * per-column display config is keyed by the base column (`revenue`), so the
 * suffix must be removed to find the inherited format. Returns the accessor
 * unchanged when no delimiter is present.
 */
export const baseColumnAccessor = (accessor: string): string => {
  const idx = accessor.indexOf(AGG_DELIMITER);
  return idx === -1 ? accessor : accessor.slice(0, idx);
};

/**
 * Looks up the `ColumnConfig` entry for a rendered accessor, falling back to
 * the base column when the accessor is an aggregate alias (`column::OP`).
 */
export const resolveColumnConfigEntry = (
  config: ColumnsConfig | null | undefined,
  accessor: string,
): ColumnConfig | undefined => {
  if (!config) return undefined;
  return config[accessor] ?? config[baseColumnAccessor(accessor)];
};

/**
 * Resolves the effective `ColumnFormat` for a rendered column.
 *
 * Tries the exact accessor, then the base column for aggregate aliases,
 * coercing legacy `{ formatType, formatOptions }` shapes on read. Falls back
 * to the primitive default when no format is configured.
 */
export const resolveColumnFormat = (
  config: ColumnsConfig | null | undefined,
  accessor: string,
  columnType?: PrimitiveDataType,
): ColumnFormat => {
  const entry = resolveColumnConfigEntry(config, accessor);
  const coerced = coerceColumnFormat(entry?.format ?? entry);
  return coerced ?? defaultFormatForPrimitive(columnType ?? "STRING");
};
